/**
 * PendingDprCard — Item 10 rebuild. Shows which departments haven't filled
 * today's DPR across the projects the current user has DPR authority on.
 * PM/Director/exec (authority='full') can nudge the relevant department via
 * "Remind"; everyone just gets "Fill" to jump straight to their own section.
 * Renders nothing when there's nothing pending for this user.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const isWithinCurrentHour = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth()
    && d.getUTCDate() === now.getUTCDate() && d.getUTCHours() === now.getUTCHours();
};

export default function PendingDprCard({ projectId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reminding, setReminding] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = () => {
    apiGet(`/dpr/pending-summary?date=${today}`).then(setData).catch(() => setData(null));
  };
  useEffect(load, []); // eslint-disable-line

  const projects = (data?.projects || []).filter((p) => !projectId || p.project_id === projectId);
  if (!data || projects.length === 0) return null;

  const remind = async (pid, dept) => {
    const key = `${pid}:${dept}`;
    setReminding(key);
    try {
      const res = await apiPost(`/dpr/${pid}/${today}/remind/${dept}`, {});
      if (res.reason === 'already_sent') toast.info(`Already reminded at ${fmtTime(res.reminded_at)}`);
      else if (res.reason === 'no_recipients') toast.error('No department recipients found for this project.');
      else toast.success(`Reminded ${res.sent} recipient${res.sent === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    } finally {
      setReminding(null);
    }
  };

  return (
    <div className="bg-white border border-amber-200 rounded-sm mb-4" data-testid="pending-dpr-card">
      <div className="px-4 py-2.5 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
        <AlertTriangle size={14} className="text-amber-600" />
        <span className="text-xs font-display font-bold uppercase tracking-wider text-amber-800">
          DPR sections pending today · {projects.length} project{projects.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {projects.map((p) => (
          <div key={p.project_id} className="px-4 py-2.5" data-testid={`pending-dpr-project-${p.project_id}`}>
            <div className="text-xs font-display font-bold text-[#0F172A] mb-1.5">{p.code ? `${p.code} · ` : ''}{p.name}</div>
            <div className="flex flex-wrap gap-2">
              {p.completion.pending.map((dept) => {
                const key = `${p.project_id}:${dept.department}`;
                const justReminded = isWithinCurrentHour(dept.reminded_at);
                return (
                  <div key={dept.department} className="flex items-center gap-1.5 border border-slate-200 rounded-sm px-2 py-1" data-testid={`pending-dpr-dept-${p.project_id}-${dept.department}`}>
                    <span className="text-[11px] text-slate-600">{dept.label}</span>
                    <button
                      onClick={() => navigate(`/app/dpr?project_id=${p.project_id}&date=${today}&dept=${dept.department}`)}
                      className="text-[10px] font-display font-bold uppercase text-[#3b4872] hover:underline"
                      data-testid={`pending-dpr-fill-${p.project_id}-${dept.department}`}
                    >
                      Fill
                    </button>
                    {p.authority === 'full' && (
                      <button
                        onClick={() => remind(p.project_id, dept.department)}
                        disabled={justReminded || reminding === key}
                        className="text-[10px] font-display font-bold uppercase text-amber-700 hover:underline disabled:text-slate-400 disabled:no-underline flex items-center gap-1"
                        data-testid={`pending-dpr-remind-${p.project_id}-${dept.department}`}
                        title={justReminded ? `Reminded at ${fmtTime(dept.reminded_at)}` : 'Send a reminder'}
                      >
                        <Send size={9} />
                        {justReminded ? `Reminded ${fmtTime(dept.reminded_at)}` : (reminding === key ? 'Sending…' : 'Remind')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
